import { pick } from 'es-toolkit';
import { isEmpty } from 'es-toolkit/compat';

import type { Nullable, SimpleObject } from '@clearverify/types';

import {
  ActiveInactive,
  type InvitedSignedAddressData,
  SignUpMethod,
  type UserRegistrationByInviteMutationVariables,
  VerifierType,
} from 'src/api';
import type { AddressFieldsFormValues } from 'src/constants/address';

import {
  CommercialVerifierBasicInfoFormFields,
  CommercialVerifierBasicInfoFormValues,
  ConsumerVerificationPreferenceFormFields,
  ConsumerVerificationPreferenceFormValues,
  EmployeeVerificationPreferenceFormFields,
  EmployerBasicInfoFormFields,
  EmployerBasicInfoFormValues,
  GovernmentVerifierBasicInfoFormFields,
  GovernmentVerifierBasicInfoFormValues,
  InitialStepFormFields,
  InitialStepFormValues,
  PEOBasicInfoFormFields,
  PEOBasicInfoFormValues,
  PEOPayrollProviderBasicInfoFormFields,
  PEOPayrollProviderBasicInfoFormValues,
  VerifierBasicInfoFormFields,
  VerifierBasicInfoFormValues,
} from '../types';

export const prepareFormDataEmployerAdmin = (
  inviteId: string,
  formStepperData: SimpleObject,
): UserRegistrationByInviteMutationVariables => {
  const userData = pick(
    formStepperData,
    Object.keys(InitialStepFormFields),
  ) as InitialStepFormValues;

  const employerData = pick(
    formStepperData,
    Object.keys(EmployerBasicInfoFormFields),
  ) as EmployerBasicInfoFormValues;

  const requestAutoAccept =
    formStepperData[EmployeeVerificationPreferenceFormFields.isAutoVerificationRequestEnabled] ===
    'required';

  return {
    inviteId,
    user: {
      firstName: userData.firstName,
      lastName: userData.lastName,
      phoneNumber: userData.phoneNumber,
      password: userData.password,
    },
    employer: {
      displayName: employerData.displayName,
      contactEmail: employerData.contactEmail,
      contactPhoneNumber: employerData.contactPhoneNumber,
      corporateStructure: employerData.corporateStructure,
      industry: employerData.industry?.value,
      numberOfEmployees: Number(employerData.numberOfEmployees),
      taxYearEndDate: employerData.taxYearEndDate,
      avatarImageFile: employerData.avatarImageFile?.file,
      disclaimer: employerData.disclaimer,

      signUpMethod: employerData.signUpMethod,

      headquarters: convertHeadquarter(employerData.headquarters),
      requestAutoAccept,
    },
  };
};

export const prepareFormDataVerifier = (
  inviteId: string,
  formStepperData: SimpleObject,
): UserRegistrationByInviteMutationVariables => {
  const isCommercialVerifier = formStepperData.accountType === 'Commercial';

  const userData = pick(
    formStepperData,
    Object.keys(InitialStepFormFields),
  ) as InitialStepFormValues;

  const commercialVerifierData = pick(
    formStepperData,
    Object.keys(CommercialVerifierBasicInfoFormFields),
  ) as CommercialVerifierBasicInfoFormValues;

  const governmentVerifierData = pick(
    formStepperData,
    Object.keys(GovernmentVerifierBasicInfoFormFields),
  ) as GovernmentVerifierBasicInfoFormValues;

  const verifierData = pick(
    formStepperData,
    Object.keys(VerifierBasicInfoFormFields),
  ) as VerifierBasicInfoFormValues;

  return {
    inviteId,
    user: {
      firstName: userData.firstName,
      lastName: userData.lastName,
      phoneNumber: userData.phoneNumber,
      password: userData.password,
    },
    verifier: {
      type: isCommercialVerifier ? VerifierType.Commercial : VerifierType.Government,

      companyDisplayName: verifierData.companyDisplayInfo,
      contactEmail: verifierData.primaryContactEmail,
      contactPhone: verifierData.primaryContactPhone,
      commercialName: verifierData.commercialName,
      fein: verifierData.fein,
      industry: verifierData.industry.value,
      avatarImageFile: verifierData.avatarImageFile?.file,
      website: verifierData.websiteUrl,
      permissiblePurposeReferenceId: verifierData.permissiblePurposeReferenceId,

      address: {
        street: String(verifierData.street),
        city: String(verifierData.city),
        zipCode: String(verifierData.zipCode),
        county: String(verifierData.county),
        state: String(verifierData.state),
      },

      ...(isCommercialVerifier
        ? commercialVerifierData
        : {
            governmentEntityName: governmentVerifierData.governmentEntityName,
            governmentType: governmentVerifierData.typeOfGovernment,
            governmentDepartment: governmentVerifierData.governmentDepartment,
          }),
    },
  };
};

export const prepareFormDataUser = (
  inviteId: string,
  formStepperData: SimpleObject,
): UserRegistrationByInviteMutationVariables => {
  const userData = pick(
    formStepperData,
    Object.keys(InitialStepFormFields),
  ) as InitialStepFormValues;

  return {
    inviteId,
    user: {
      firstName: userData.firstName,
      lastName: userData.lastName,
      phoneNumber: userData.phoneNumber,
      password: userData.password,
      ...userData.homeAddress,
    },
  };
};

export const prepareFormDataConsumer = (
  inviteId: string,
  formStepperData: SimpleObject,
): UserRegistrationByInviteMutationVariables => {
  const userData = pick(
    formStepperData,
    Object.keys(InitialStepFormFields),
  ) as InitialStepFormValues;

  const consumerData = pick(
    formStepperData,
    Object.keys(ConsumerVerificationPreferenceFormFields),
  ) as ConsumerVerificationPreferenceFormValues;

  return {
    inviteId,
    user: {
      firstName: userData.firstName,
      lastName: userData.lastName,
      phoneNumber: userData.phoneNumber,
      password: userData.password,
      ...userData.homeAddress,
    },
    consumer: {
      requestAutoApprove: consumerData.isAutoVerificationRequestEnabled !== ActiveInactive.Inactive,
    },
  };
};

export const prepareFormDataCommercialVerifier = (
  inviteId: string,
  formStepperData: SimpleObject,
): UserRegistrationByInviteMutationVariables => {
  const userData = pick(
    formStepperData,
    Object.keys(InitialStepFormFields),
  ) as InitialStepFormValues;

  return {
    inviteId,
    user: {
      firstName: userData.firstName,
      lastName: userData.lastName,
      phoneNumber: userData.phoneNumber,
      password: userData.password,
    },
  };
};

export const prepareFormDataPEO = (
  inviteId: string,
  formStepperData: SimpleObject,
): UserRegistrationByInviteMutationVariables => {
  const userData = pick(
    formStepperData,
    Object.keys(InitialStepFormFields),
  ) as InitialStepFormValues;

  const peoData = pick(
    formStepperData,
    Object.keys(PEOBasicInfoFormFields),
  ) as PEOBasicInfoFormValues;

  const requestAutoAccept =
    formStepperData[EmployeeVerificationPreferenceFormFields.isAutoVerificationRequestEnabled] ===
    'required';

  const isAdditionalStaff = isEmpty(peoData);

  return {
    inviteId,
    user: {
      firstName: userData.firstName,
      lastName: userData.lastName,
      phoneNumber: userData.phoneNumber,
      password: userData.password,
    },
    ...(!isAdditionalStaff
      ? {
          peo: {
            displayName: peoData.displayName,
            contactEmail: peoData.contactEmail,
            contactPhoneNumber: peoData.contactPhoneNumber,
            corporateStructure: peoData.corporateStructure,
            industry: peoData.industry?.value,
            numberOfEmployees: Number(peoData.numberOfEmployees),
            fein: peoData.fein,
            taxYearEndDate: peoData.taxYearEndDate,
            avatarImageFile: peoData.avatarImageFile?.file,
            disclaimer: peoData.disclaimer,

            signUpMethod: peoData.signUpMethod,

            headquarters: convertHeadquarter(peoData.headquarters),
            requestAutoAccept,
          },
        }
      : {}),
  };
};

export const prepareFormDataPEOPayrollProvider = (
  inviteId: string,
  formStepperData: SimpleObject,
): UserRegistrationByInviteMutationVariables => {
  const userData = pick(
    formStepperData,
    Object.keys(InitialStepFormFields),
  ) as InitialStepFormValues;

  const peoPayrollProvider = pick(
    formStepperData,
    Object.keys(PEOPayrollProviderBasicInfoFormFields),
  ) as PEOPayrollProviderBasicInfoFormValues;

  const requestAutoAccept =
    formStepperData[EmployeeVerificationPreferenceFormFields.isAutoVerificationRequestEnabled] ===
    'required';

  const isAdditionalStaff = isEmpty(peoPayrollProvider);

  return {
    inviteId,
    user: {
      firstName: userData.firstName,
      lastName: userData.lastName,
      phoneNumber: userData.phoneNumber,
      password: userData.password,
    },
    ...(!isAdditionalStaff
      ? {
          peoPayrollProvider: {
            displayName: peoPayrollProvider.displayName,
            contactEmail: peoPayrollProvider.contactEmail,
            contactPhoneNumber: peoPayrollProvider.contactPhoneNumber,
            corporateStructure: peoPayrollProvider.corporateStructure,
            industry: peoPayrollProvider.industry?.value,
            numberOfEmployees: Number(peoPayrollProvider.numberOfEmployees),
            fein: peoPayrollProvider.fein,
            taxYearEndDate: peoPayrollProvider.taxYearEndDate,
            avatarImageFile: peoPayrollProvider.avatarImageFile?.file,
            disclaimer: peoPayrollProvider.disclaimer,

            signUpMethod: peoPayrollProvider.signUpMethod,

            headquarters: convertHeadquarter(peoPayrollProvider.headquarters),
            requestAutoAccept,
          },
        }
      : {}),
  };
};

export const convertHeadquarter = (
  headquarter: Nullable<AddressFieldsFormValues>,
): Nullable<InvitedSignedAddressData> => {
  if (!headquarter) return null;

  return {
    city: String(headquarter.city),
    county: headquarter.county,
    state: String(headquarter.state),
    street: String(headquarter.street),
    zipCode: String(headquarter.zipCode),
  };
};

export const prepareSignUpMethod = (signUpMethod: string) => {
  if (signUpMethod === SignUpMethod.Pin) return SignUpMethod.Pin;
  return SignUpMethod.Ssn;
};

export type PrepareFormDataType = (
  id: string,
  formData: SimpleObject,
) => UserRegistrationByInviteMutationVariables;
